import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { X, UserPlus, Users, ChevronDown } from 'lucide-react'
import { grantPermission, getPermissions } from '../../services/api'
import type { Permission } from '../../types'
import { granteeLabel, granteeInitial } from '../../utils/collaborators'

interface ShareModalProps {
  resourceId: string
  resourceType: 'project' | 'folder' | 'document'
  projectId?: string
  onClose: () => void
}

export default function ShareModal({ resourceId, resourceType, projectId, onClose }: ShareModalProps) {
  // Clear-success-message timer ref so we can cancel on unmount (prevents
  // setSuccessMsg on unmounted component — Story #2645 Task #2646).
  const successClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (successClearTimerRef.current) clearTimeout(successClearTimerRef.current)
    }
  }, [])

  // For folder/document sharing: show collaborator picker from project members
  const showCollaboratorPicker = resourceType !== 'project' && !!projectId

  // Fetch project collaborators for the picker
  const { data: projectCollaborators } = useQuery({
    queryKey: ['permissions', projectId],
    queryFn: () => getPermissions(projectId!),
    enabled: showCollaboratorPicker,
  })

  // Collaborator picker state
  const [selectedCollaborators, setSelectedCollaborators] = useState<Set<string>>(new Set())
  const [collaboratorPermissions, setCollaboratorPermissions] = useState<Record<string, 'view' | 'comment' | 'edit'>>({})

  // Email-based state (for project-level sharing or fallback)
  const [email, setEmail] = useState('')
  const [permissionLevel, setPermissionLevel] = useState<'view' | 'comment' | 'edit' | 'manager'>('view')
  const [allowDownload, setAllowDownload] = useState(false)
  const [allowPrint, setAllowPrint] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [error, setError] = useState('')
  const [showEmailFallback, setShowEmailFallback] = useState(false)

  // Advanced granular permissions (folder sharing only)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const granularDefaults: Record<string, Record<string, boolean>> = {
    view: { can_create: false, can_read: true, can_edit: false, can_delete: false, can_manage_collaborators: false },
    comment: { can_create: false, can_read: true, can_edit: false, can_delete: false, can_manage_collaborators: false },
    edit: { can_create: true, can_read: true, can_edit: true, can_delete: false, can_manage_collaborators: false },
    manager: { can_create: true, can_read: true, can_edit: true, can_delete: true, can_manage_collaborators: true },
  }
  const [granularPerms, setGranularPerms] = useState(granularDefaults['view'])

  const toggleCollaborator = (granteeId: string) => {
    setSelectedCollaborators((prev) => {
      const next = new Set(prev)
      if (next.has(granteeId)) next.delete(granteeId)
      else next.add(granteeId)
      return next
    })
  }

  const setCollabPerm = (granteeId: string, level: 'view' | 'comment' | 'edit') => {
    setCollaboratorPermissions((prev) => ({ ...prev, [granteeId]: level }))
  }

  // Batch share with selected collaborators
  const batchShareMutation = useMutation({
    mutationFn: async () => {
      const collabs = projectCollaborators?.filter((c) => selectedCollaborators.has(c.grantee_id)) || []
      for (const collab of collabs) {
        const level = collaboratorPermissions[collab.grantee_id] || 'view'
        await grantPermission({
          resource_id: resourceId,
          grantee_email: collab.grantee_email || collab.grantee_id,
          permission_level: level,
          allow_download: allowDownload,
          allow_print: allowPrint,
          resource_type: resourceType,
          ...(resourceType === 'folder' && showAdvanced ? granularPerms : {}),
        })
      }
    },
    onSuccess: () => {
      setSuccessMsg(`Shared with ${selectedCollaborators.size} collaborator(s)`)
      setSelectedCollaborators(new Set())
      setTimeout(() => setSuccessMsg(''), 3000)
    },
    onError: (err: any) => setError(err?.response?.data?.error || 'Failed to share with some collaborators.'),
  })

  // Email-based share (project-level or fallback)
  const emailShareMutation = useMutation({
    mutationFn: () =>
      grantPermission({
        resource_id: resourceId,
        grantee_email: email,
        permission_level: permissionLevel,
        allow_download: allowDownload,
        allow_print: allowPrint,
        resource_type: resourceType,
        ...(resourceType === 'folder' && showAdvanced ? granularPerms : {}),
      }),
    onSuccess: () => {
      setSuccessMsg('Shared successfully!')
      setEmail('')
      if (successClearTimerRef.current) clearTimeout(successClearTimerRef.current)
      successClearTimerRef.current = setTimeout(() => setSuccessMsg(''), 3000)
    },
    onError: (err: any) => setError(err?.response?.data?.error || 'Failed to share. Please check the email and try again.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[#0c4a6e] rounded-xl border border-white/20 backdrop-blur-md shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {resourceType === 'project' ? 'Add Collaborator' : `Share ${resourceType}`}
          </h3>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white/60">
            <X size={18} />
          </button>
        </div>

        {successMsg && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-400/30 rounded-lg text-sm text-green-300">
            {successMsg}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-lg text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Collaborator picker mode (for folder/document sharing) */}
        {showCollaboratorPicker && !showEmailFallback ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                <Users size={14} className="inline mr-1" /> Project Collaborators
              </label>
              {!projectCollaborators?.length ? (
                <p className="text-sm text-white/60 py-3">
                  No project collaborators yet. Add collaborators at the project level first.
                </p>
              ) : (
                <div className="border border-white/20 rounded-lg max-h-48 overflow-y-auto divide-y divide-white/10">
                  {projectCollaborators.map((collab: Permission) => (
                    <label key={collab.grantee_id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCollaborators.has(collab.grantee_id)}
                        onChange={() => toggleCollaborator(collab.grantee_id)}
                        className="w-4 h-4 text-iflow-500 border-white/30 rounded focus:ring-iflow-500 bg-white/10"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-iflow-500/20 text-iflow-300 flex items-center justify-center text-xs font-medium flex-shrink-0">
                            {granteeInitial(collab)}
                          </div>
                          <span className="text-sm text-white/80 truncate">{granteeLabel(collab)}</span>
                        </div>
                      </div>
                      <select
                        value={collaboratorPermissions[collab.grantee_id] || 'view'}
                        onChange={(e) => { e.stopPropagation(); setCollabPerm(collab.grantee_id, e.target.value as 'view' | 'comment' | 'edit') }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs border border-white/20 rounded px-1.5 py-1 focus:ring-1 focus:ring-iflow-500 outline-none bg-white/10 text-white"
                      >
                        <option value="view">View</option>
                        <option value="comment">Comment</option>
                        <option value="edit">Edit</option>
                      </select>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={allowDownload}
                  onChange={(e) => setAllowDownload(e.target.checked)}
                  className="w-4 h-4 text-iflow-500 border-white/30 rounded focus:ring-iflow-500 bg-white/10"
                />
                Allow download
              </label>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={allowPrint}
                  onChange={(e) => setAllowPrint(e.target.checked)}
                  className="w-4 h-4 text-iflow-500 border-white/30 rounded focus:ring-iflow-500 bg-white/10"
                />
                Allow print
              </label>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setShowEmailFallback(true)}
                className="text-xs text-sky-300 hover:text-sky-200 font-medium"
              >
                + Invite by email instead
              </button>
              <div className="flex gap-3">
                <button onClick={onClose} className="px-4 py-2 text-sm text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20">
                  Cancel
                </button>
                <button
                  onClick={() => batchShareMutation.mutate()}
                  disabled={selectedCollaborators.size === 0 || batchShareMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
                >
                  <UserPlus size={14} /> {batchShareMutation.isPending ? 'Sharing...' : `Share (${selectedCollaborators.size})`}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Email-based mode (project-level or fallback) */
          <div className="space-y-4">
            {showEmailFallback && (
              <button
                onClick={() => setShowEmailFallback(false)}
                className="text-xs text-sky-300 hover:text-sky-200 font-medium"
              >
                &larr; Back to collaborator list
              </button>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
              <input
                type="email"
                value={email}
                // UI-4: clear any stale "user not found" error as soon as the
                // field is edited/cleared, so the error doesn't linger.
                onChange={(e) => { setEmail(e.target.value); if (error) setError('') }}
                className="w-full px-3 py-2 light-input text-sm outline-none"
                placeholder="collaborator@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Permission Level</label>
              <select
                value={permissionLevel}
                onChange={(e) => {
                  const level = e.target.value as 'view' | 'comment' | 'edit' | 'manager'
                  setPermissionLevel(level)
                  setGranularPerms(granularDefaults[level] || granularDefaults['view'])
                }}
                className="w-full px-3 py-2 light-input text-sm outline-none"
              >
                <option value="view">View only</option>
                <option value="comment">Can comment</option>
                <option value="edit">Can edit</option>
                {resourceType === 'project' && <option value="manager">Manager — can manage collaborators</option>}
              </select>
            </div>

            {/* Advanced Permissions (folder sharing only) */}
            {resourceType === 'folder' && (
              <div className="border border-white/20 rounded-lg">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center justify-between w-full px-3 py-2 text-sm text-white/60 hover:bg-white/5"
                >
                  <span className="font-medium">Advanced Permissions</span>
                  <ChevronDown size={14} className={`transition-transform ${showAdvanced ? '' : '-rotate-90'}`} />
                </button>
                {showAdvanced && (
                  <div className="px-3 pb-3 space-y-2 border-t border-white/10 pt-2">
                    {[
                      { key: 'can_create', label: 'Create documents' },
                      { key: 'can_read', label: 'Read documents' },
                      { key: 'can_edit', label: 'Edit documents' },
                      { key: 'can_delete', label: 'Delete documents' },
                      { key: 'can_manage_collaborators', label: 'Manage collaborators' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 text-sm text-white/80">
                        <input
                          type="checkbox"
                          checked={granularPerms[key] ?? false}
                          onChange={(e) => setGranularPerms((prev) => ({ ...prev, [key]: e.target.checked }))}
                          className="w-4 h-4 text-iflow-500 border-white/30 rounded focus:ring-iflow-500 bg-white/10"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={allowDownload}
                  onChange={(e) => setAllowDownload(e.target.checked)}
                  className="w-4 h-4 text-iflow-500 border-white/30 rounded focus:ring-iflow-500 bg-white/10"
                />
                Allow download
              </label>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={allowPrint}
                  onChange={(e) => setAllowPrint(e.target.checked)}
                  className="w-4 h-4 text-iflow-500 border-white/30 rounded focus:ring-iflow-500 bg-white/10"
                />
                Allow print
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={onClose} className="px-4 py-2 text-sm text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20">
                Cancel
              </button>
              <button
                onClick={() => emailShareMutation.mutate()}
                disabled={!email.trim() || emailShareMutation.isPending}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                <UserPlus size={14} /> {emailShareMutation.isPending ? 'Sharing...' : resourceType === 'project' ? 'Add' : 'Share'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
